import { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import NotificationBell from '../../components/NotificationBell'
import {
  BookOpen,
  Users,
  FileCheck,
  Calendar,
  LogOut,
  Home,
  Upload,
  ClipboardCheck,
  Menu,
  X,
  MessageSquare,
  User,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  ClipboardList,
  Sun,
  Moon,
  Sparkles,
  Folder,
  Layers,
} from 'lucide-react'
import { useTheme } from '../../context/ThemeContext'
import MyCourses from './MyCourses'
import CourseDetails from './CourseDetails'
import Students from './Students'
import Grading from './Grading'
import Attendance from './Attendance'
import SelfAttendanceQueue from './SelfAttendanceQueue'
import StudentProgress from './StudentProgress'
import UploadContent from './UploadContent'
import MessagesPage from '../../components/MessagesPage'
import TrainerProfile from './Profile'
import LoadingSpinner from '../../components/LoadingSpinner'
import AITrainerCopilot from './AITrainerCopilot'
import DigitalLabSubmissions from './DigitalLabSubmissions'
import TrainerProjectLab from './TrainerProjectLab'

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function DashboardHome() {
  const { user } = useAuthStore()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/trainers/dashboard')
      .then((res) => { setDashboardData(res.data); setLoading(false) })
      .catch((err) => { console.error(err); setLoading(false) })
  }, [])

  if (loading) return <LoadingSpinner label="Loading dashboard" />

  const totalStudents = dashboardData?.courses?.reduce((sum, c) => sum + parseInt(c.enrolled_students || 0), 0) || 0
  const totalLessons = dashboardData?.courses?.reduce((sum, c) => sum + parseInt(c.total_lessons || 0), 0) || 0
  const pendingCount = parseInt(dashboardData?.pending_assignments || 0)

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="space-y-10">
      {/* Welcome */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">{greeting}</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">
            {user?.first_name} {user?.last_name}
          </h1>
          <p className="font-sans text-sm text-cinema-muted max-w-xl mt-2">
            {dashboardData?.courses?.length
              ? `You have ${dashboardData.courses.length} course${dashboardData.courses.length !== 1 ? 's' : ''} and ${totalStudents} student${totalStudents !== 1 ? 's' : ''} enrolled.`
              : 'No courses assigned yet. Contact admin to get your courses set up.'}
            {pendingCount > 0 && (
              <span className="ml-1 text-red-400 font-medium">
                {pendingCount} submission{pendingCount !== 1 ? 's' : ''} awaiting grading.
              </span>
            )}
          </p>
          <p className="font-sans text-xs sm:text-sm text-cinema-muted/90 mt-2">
            Open a course to access Class Control and Trainer Copilot.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'My Courses', value: dashboardData?.courses?.length || 0, link: '/trainer/courses' },
          { label: 'Total Students', value: totalStudents, link: '/trainer/students' },
          { label: 'Pending Grading', value: pendingCount, link: '/trainer/grading' },
          { label: 'Total Lessons', value: totalLessons, link: '/trainer/content' },
        ].map(({ label, value, link }) => (
          <Link key={label} to={link} className="cinema-stat group">
            <span className="cinema-stat-label group-hover:text-cinema-gold transition-colors">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 pt-8 border-t border-white/5">
        {/* My Courses */}
        <div>
          <div className="cinema-section-header">
            <h2 className="cinema-section-title flex items-center gap-2">
              <BookOpen size={16} className="text-cinema-glow" /> My Courses
            </h2>
            <Link to="/trainer/courses" className="cinema-btn-text text-xs sm:text-sm font-medium">View all →</Link>
          </div>

          {!dashboardData?.courses?.length ? (
            <div className="cinema-empty">
              <BookOpen size={32} className="mx-auto mb-3 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No courses assigned yet</p>
            </div>
          ) : (
            <div className="cinema-list">
              {dashboardData.courses.map(course => {
                const pct = course.total_lessons > 0
                  ? Math.round((course.published_lessons / course.total_lessons) * 100)
                  : 0
                return (
                  <Link
                    key={course.id}
                    to={`/trainer/courses/${course.id}`}
                    className="cinema-list-row group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm sm:text-base text-white font-medium truncate mb-1">{course.title}</p>
                      <div className="flex items-center gap-3 mb-1.5 text-xs sm:text-sm text-cinema-muted font-medium">
                        <span className="font-sans">{course.enrolled_students} students</span>
                        <span className="font-sans">· {course.published_lessons}/{course.total_lessons} lessons</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/5">
                        <div className="bg-cinema-glow h-1.5 transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 flex-shrink-0 transition-colors" />
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* Pending Grading */}
        <div>
          <div className="cinema-section-header">
            <h2 className="cinema-section-title flex items-center gap-2">
              <FileCheck size={16} className="text-cinema-glow" /> Pending Grading
              {pendingCount > 0 && (
                <span className="px-2.5 py-0.5 text-xs font-sans font-semibold uppercase tracking-wide bg-red-500/10 text-red-400 border border-red-500/25 rounded-sm">
                  {pendingCount}
                </span>
              )}
            </h2>
            <Link to="/trainer/grading" className="cinema-btn-text text-xs sm:text-sm font-medium">Grade all →</Link>
          </div>

          {!dashboardData?.recent_submissions?.length ? (
            <div className="cinema-empty">
              <CheckCircle size={32} className="mx-auto mb-3 text-green-500/20" />
              <p className="font-sans text-base text-white mb-1">All caught up!</p>
              <p className="font-sans text-sm text-cinema-muted">No submissions waiting to be graded.</p>
            </div>
          ) : (
            <div className="cinema-list">
              {dashboardData.recent_submissions.map(sub => (
                <div key={sub.id} className="cinema-list-row">
                  <div className="w-8 h-8 rounded-sm bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle size={14} className="text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm sm:text-base text-white truncate mb-0.5 font-medium">{sub.assignment_title}</p>
                    <p className="font-sans text-xs sm:text-sm text-cinema-muted truncate">
                      {sub.first_name} {sub.last_name} · {sub.course_title}
                    </p>
                  </div>
                  <span className="font-sans text-xs text-cinema-muted flex-shrink-0 flex items-center gap-1.5 mt-0.5">
                    <Clock size={12} /> {timeAgo(sub.submitted_at)}
                  </span>
                </div>
              ))}
              {pendingCount > dashboardData.recent_submissions.length && (
                <Link to="/trainer/grading" className="block text-center font-sans text-xs text-cinema-glow hover:text-cinema-amber py-4 transition-colors">
                  +{pendingCount - dashboardData.recent_submissions.length} more — view all
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header">
          <h2 className="cinema-section-title">Quick Actions</h2>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {[
            { to: '/trainer/attendance', icon: Calendar, label: 'Take Attendance' },
            { to: '/trainer/grading', icon: FileCheck, label: 'Grade Submissions' },
            { to: '/trainer/content', icon: Upload, label: 'Upload Content' },
            { to: '/trainer/progress', icon: ClipboardCheck, label: 'Student Progress' },
          ].map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              className="inline-flex items-center gap-2 text-sm text-cinema-soft hover:text-cinema-gold font-sans transition-colors"
            >
              <Icon size={14} /> {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function TrainerDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const navLinks = [
    { to: '/trainer/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/trainer/courses', icon: BookOpen, label: 'My Courses' },
    { to: '/trainer/students', icon: Users, label: 'Students' },
    { to: '/trainer/progress', icon: ClipboardCheck, label: 'Student Progress' },
    { to: '/trainer/grading', icon: FileCheck, label: 'Grading' },
    { to: '/trainer/digital-lab/submissions', icon: Folder, label: 'Project Reviews' },
    { to: '/trainer/project-lab', icon: Layers, label: 'Project Lab Reviews', highlight: true },
    { to: '/trainer/attendance', icon: Calendar, label: 'Attendance' },
    { to: '/trainer/self-attendance', icon: ClipboardList, label: 'Self-Attendance', highlight: true },
    { to: '/trainer/content', icon: Upload, label: 'Upload Content' },
    { to: '/trainer/copilot', icon: Sparkles, label: 'AI Copilot', highlight: true },
    { to: '/trainer/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/trainer/profile', icon: User, label: 'My Profile' },
  ]

  return (
    <div className={`cinema-page min-h-screen ${darkMode ? '' : 'admin-light'}`}>
      <div className="cinema-grain" aria-hidden="true" />
      {darkMode && (
        <>
          <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-30 fixed" />
          <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-0 -left-20 opacity-20 fixed" />
        </>
      )}

      {/* Top Nav */}
      <nav className="sticky top-0 z-30 cinema-glass-strong">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 lg:hidden text-cinema-muted hover:text-white transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="SMA Trainer Portal"
                className="h-8"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-display text-white font-medium text-sm leading-none">Trainer Portal</span>
                <span className="cinema-label mt-1">Synergy Multimedia Academy</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/trainer/profile"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-soft hover:text-white transition-all"
                title="My profile"
              >
                <User size={14} className="text-cinema-gold" />
                <span className="font-sans text-xs">{user?.first_name} {user?.last_name}</span>
              </Link>
              <NotificationBell />
              <button
                onClick={toggleDarkMode}
                className="flex items-center justify-center w-9 h-9 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-muted hover:text-cinema-gold transition-all"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-red-500/40 text-cinema-muted hover:text-red-400 text-xs font-sans transition-all"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed top-16 left-0 bottom-0 w-60 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
          cinema-glass-strong border-r border-white/5 p-4
          lg:static lg:translate-x-0 lg:flex-shrink-0 lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="space-y-1">
            {navLinks.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-all duration-200 ${
                  location.pathname === to || (to !== '/trainer/dashboard' && location.pathname.startsWith(`${to}/`))
                    ? 'bg-cinema-glow/10 text-cinema-glow border-l-2 border-cinema-glow'
                    : 'text-cinema-muted hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`}
              >
                <Icon size={16} />
                {label}
              </Link>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0 relative">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="courses" element={<MyCourses />} />
            <Route path="courses/:courseId" element={<CourseDetails />} />
            <Route path="students" element={<Students />} />
            <Route path="progress" element={<StudentProgress />} />
            <Route path="grading" element={<Grading />} />
            <Route path="digital-lab/submissions" element={<DigitalLabSubmissions />} />
            <Route path="digital-lab/submissions/:id" element={<DigitalLabSubmissions />} />
            <Route path="project-lab" element={<TrainerProjectLab />} />
            <Route path="project-lab/:enrollmentId" element={<TrainerProjectLab />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="self-attendance" element={<SelfAttendanceQueue />} />
            <Route path="content" element={<UploadContent />} />
            <Route path="copilot" element={<AITrainerCopilot />} />
            <Route path="messages" element={<MessagesPage contactsEndpoint="/trainers/contacts" title="Messages" />} />
            <Route path="profile" element={<TrainerProfile />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
